import React, { useState, useEffect } from 'react';
import { FaSearch, FaDownload, FaEye, FaFilter, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import BOLayout from '@/pages/back-office/BOLayout';
import Modal from '@/components/Modal';

const BAAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  
  // Vérification des permissions
  const userRole = JSON.parse(localStorage.getItem('role') || '""');
  const canCleanLogs = ['admin', 'super-admin'].includes(userRole);
  const canExportLogs = ['admin', 'super-admin'].includes(userRole);
  
  if (!['admin', 'super-admin'].includes(userRole)) {
    return (
      <BOLayout>
        <div className="container mx-auto py-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </div>
        </div>
      </BOLayout>
    );
  }
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    restaurantId: '',
    userId: '',
    entity: '',
    action: '',
    level: '',
    dateStart: '',
    dateEnd: '',
    search: ''
  });

  // États pour la modal de détails
  const [showModal, setShowModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // États pour la pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0
  });

  useEffect(() => {
    fetchLogs();
    fetchStats();
    fetchFilterOptions();
  }, [filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(`/api/back-office/audit/logs?${params}`);
      setLogs(response.data.logs);
      setPagination(response.data.pagination);
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/back-office/audit/logs/stats', {
        params: { restaurantId: filters.restaurantId }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques:', err);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get('/api/back-office/audit/logs/filter-options', {
        params: { restaurantId: filters.restaurantId }
      });
      setFilterOptions(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des options de filtre:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset à la première page lors du changement de filtre
    }));
  };

  const resetFilters = () => {
    setFilters({
      page: 1,
      limit: 50,
      restaurantId: '',
      userId: '',
      entity: '',
      action: '',
      level: '',
      dateStart: '',
      dateEnd: '',
      search: ''
    });
  };

  const exportLogs = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== 'page' && key !== 'limit') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`/api/back-office/audit/logs/export?${params}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'audit_logs.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Erreur lors de l\'export');
      console.error(err);
    }
  };

  const cleanOldLogs = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer les logs de plus de 30 jours ?')) {
      try {
        await axios.delete('/api/back-office/audit/logs/clean');
        fetchLogs();
        alert('Logs anciens supprimés avec succès');
      } catch (err) {
        setError('Erreur lors de la suppression');
        console.error(err);
      }
    }
  };

  const showLogDetails = (log) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  const getLevelBadge = (level) => {
    const variants = {
      'INFO': 'bg-green-100 text-green-800',
      'WARNING': 'bg-yellow-100 text-yellow-800',
      'ERROR': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${variants[level] || 'bg-gray-100 text-gray-800'}`}>
        {level}
      </span>
    );
  };

  const getEntityBadge = (entity) => {
    const variants = {
      'ORDER': 'bg-blue-100 text-blue-800',
      'PRODUCT': 'bg-purple-100 text-purple-800',
      'STOCK': 'bg-orange-100 text-orange-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${variants[entity] || 'bg-gray-100 text-gray-800'}`}>
        {entity}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR');
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  return (
    <BOLayout>
      <div className="container mx-auto py-4">
        <div className="mb-4">
          <h2 className="text-2xl font-bold">Journalisation des Actions</h2>
          <p className="text-gray-600">
            Suivi des actions administratives sur les commandes et stocks
          </p>
        </div>

        {/* Statistiques */}
        {stats.total > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <h5 className="text-2xl font-bold">{stats.total}</h5>
              <small className="text-gray-500">Total des logs</small>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <h5 className="text-2xl font-bold text-green-600">{stats.byLevel?.INFO || 0}</h5>
              <small className="text-gray-500">Informations</small>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <h5 className="text-2xl font-bold text-yellow-600">{stats.byLevel?.WARNING || 0}</h5>
              <small className="text-gray-500">Avertissements</small>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <h5 className="text-2xl font-bold text-red-600">{stats.byLevel?.ERROR || 0}</h5>
              <small className="text-gray-500">Erreurs</small>
            </div>
          </div>
        )}

        {/* Filtres */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold flex items-center">
              <FaFilter className="mr-2" />
              Filtres
            </h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Recherche</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <FaSearch className="absolute left-2 top-3 text-gray-400" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Entité</label>
                <select
                  value={filters.entity}
                  onChange={(e) => handleFilterChange('entity', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Toutes</option>
                  {filterOptions.entities?.map(entity => (
                    <option key={entity} value={entity}>{entity}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Action</label>
                <select
                  value={filters.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Toutes</option>
                  {filterOptions.actions?.map(action => (
                    <option key={action} value={action}>{action}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Niveau</label>
                <select
                  value={filters.level}
                  onChange={(e) => handleFilterChange('level', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tous</option>
                  {filterOptions.levels?.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Utilisateur</label>
                <select
                  value={filters.userId}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tous</option>
                  {filterOptions.users?.map(user => (
                    <option key={user.id} value={user.id}>{user.email}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Date début</label>
                <input
                  type="date"
                  value={filters.dateStart}
                  onChange={(e) => handleFilterChange('dateStart', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date fin</label>
                <input
                  type="date"
                  value={filters.dateEnd}
                  onChange={(e) => handleFilterChange('dateEnd', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                >
                  Réinitialiser
                </button>
                {canExportLogs && (
                  <button
                    onClick={exportLogs}
                    className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center"
                  >
                    <FaDownload className="mr-1" />
                    Export CSV
                  </button>
                )}
                {canCleanLogs && (
                  <button
                    onClick={cleanOldLogs}
                    className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center"
                  >
                    <FaTrash className="mr-1" />
                    Nettoyer
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tableau des logs */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">Logs d'audit ({pagination.total} résultats)</h3>
            {/* Message d'aide si peu de logs sont visibles */}
            {!loading && logs.length > 0 && pagination.total < 100 && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Conseil:</strong> Si vous ne voyez que des logs récents, essayez de:
                    </p>
                    <ul className="mt-1 text-sm text-blue-600 list-disc list-inside">
                      <li>Sélectionner une plage de dates plus large dans les filtres</li>
                      <li>Augmenter le nombre de logs par page (sélecteur ci-dessous)</li>
                      <li>Vérifier si des logs anciens ont été supprimés automatiquement</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="p-4">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entité</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Niveau</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {logs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(log.created_at)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {log.user_email}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {log.action}
                            </span>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            {getEntityBadge(log.entity)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {log.description}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            {getLevelBadge(log.level)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <button
                              onClick={() => showLogDetails(log)}
                              className="text-blue-600 hover:text-blue-900 transition-colors"
                            >
                              <FaEye />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.total > pagination.limit && (
                  <div className="flex flex-col items-center mt-6 space-y-4">
                    {/* Navigation par pages */}
                    <nav className="flex space-x-2">
                      <button
                        disabled={pagination.page === 1}
                        onClick={() => handlePageChange(pagination.page - 1)}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Précédent
                      </button>
                      
                      {/* Affichage intelligent des numéros de page */}
                      {(() => {
                        const totalPages = Math.ceil(pagination.total / pagination.limit);
                        const currentPage = pagination.page;
                        const maxPagesToShow = 7;
                        
                        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
                        
                        if (endPage - startPage + 1 < maxPagesToShow) {
                          startPage = Math.max(1, endPage - maxPagesToShow + 1);
                        }
                        
                        const pages = [];
                        
                        // Première page
                        if (startPage > 1) {
                          pages.push(
                            <button
                              key={1}
                              onClick={() => handlePageChange(1)}
                              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                              1
                            </button>
                          );
                          if (startPage > 2) {
                            pages.push(
                              <span key="start-ellipsis" className="px-3 py-2 text-sm text-gray-500">
                                ...
                              </span>
                            );
                          }
                        }
                        
                        // Pages visibles
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              className={`px-3 py-2 text-sm font-medium rounded-md ${
                                i === currentPage
                                  ? 'text-white bg-blue-600 border border-blue-600'
                                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {i}
                            </button>
                          );
                        }
                        
                        // Dernière page
                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span key="end-ellipsis" className="px-3 py-2 text-sm text-gray-500">
                                ...
                              </span>
                            );
                          }
                          pages.push(
                            <button
                              key={totalPages}
                              onClick={() => handlePageChange(totalPages)}
                              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                              {totalPages}
                            </button>
                          );
                        }
                        
                        return pages;
                      })()}
                      
                      <button
                        disabled={pagination.page === Math.ceil(pagination.total / pagination.limit)}
                        onClick={() => handlePageChange(pagination.page + 1)}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Suivant
                      </button>
                    </nav>
                    
                    {/* Informations de pagination */}
                    <div className="text-sm text-gray-500 text-center">
                      Page {pagination.page} sur {Math.ceil(pagination.total / pagination.limit)} 
                      ({((pagination.page - 1) * pagination.limit) + 1} à {Math.min(pagination.page * pagination.limit, pagination.total)} sur {pagination.total} résultats)
                    </div>
                    
                    {/* Bouton pour afficher plus de logs par page */}
                    <div className="flex items-center space-x-4">
                      <label className="text-sm text-gray-600">
                        Logs par page:
                        <select
                          value={filters.limit}
                          onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                          className="ml-2 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value={200}>200</option>
                          <option value={500}>500</option>
                        </select>
                      </label>
                      
                      {pagination.hasMore && (
                        <button
                          onClick={() => handleFilterChange('limit', Math.min(filters.limit * 2, 1000))}
                          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
                        >
                          Charger plus de logs
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Modal de détails */}
        {showModal && selectedLog && (
          <Modal
            title="Détails du log d'audit"
            setShowModal={setShowModal}
            type="info"
            showFooterButtons={false}
            maxWidth="max-w-6xl"
            customButtons={
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
              >
                Fermer
              </button>
            }
          >
            <div className="text-left space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Informations générales */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-3">Informations générales</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-600">Date:</span>
                    <p className="text-sm text-gray-900">{formatDate(selectedLog.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Utilisateur:</span>
                    <p className="text-sm text-gray-900">{selectedLog.user_email}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Action:</span>
                    <div className="mt-1">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {selectedLog.action}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Entité:</span>
                    <div className="mt-1">{getEntityBadge(selectedLog.entity)}</div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Niveau:</span>
                    <div className="mt-1">{getLevelBadge(selectedLog.level)}</div>
                  </div>
                  {selectedLog.ip_address && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Adresse IP:</span>
                      <p className="text-sm text-gray-900">{selectedLog.ip_address}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Description</h4>
                <p className="text-sm text-gray-700">{selectedLog.description}</p>
              </div>
              
              {/* État avant et après */}
              {(selectedLog.data_before || selectedLog.data_after) && (
                <div className="space-y-4">
                  {selectedLog.data_before && (
                    <div className="bg-red-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-red-800 mb-3 flex items-center">
                        <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                        État avant modification
                      </h4>
                      <div className="bg-white border border-red-200 rounded-md">
                        <pre className="p-4 text-sm text-gray-800 overflow-x-auto max-h-60 overflow-y-auto whitespace-pre-wrap break-words">
                          {JSON.stringify(selectedLog.data_before, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                  
                  {selectedLog.data_after && (
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                        <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                        État après modification
                      </h4>
                      <div className="bg-white border border-green-200 rounded-md">
                        <pre className="p-4 text-sm text-gray-800 overflow-x-auto max-h-60 overflow-y-auto whitespace-pre-wrap break-words">
                          {JSON.stringify(selectedLog.data_after, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Métadonnées techniques */}
              {(selectedLog.entity_id || selectedLog.restaurant_id) && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">Métadonnées techniques</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedLog.entity_id && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">ID Entité:</span>
                        <p className="text-sm text-gray-900 font-mono">{selectedLog.entity_id}</p>
                      </div>
                    )}
                    {selectedLog.restaurant_id && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">ID Restaurant:</span>
                        <p className="text-sm text-gray-900 font-mono">{selectedLog.restaurant_id}</p>
                      </div>
                    )}
                    {selectedLog.user_id && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">ID Utilisateur:</span>
                        <p className="text-sm text-gray-900 font-mono">{selectedLog.user_id}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Modal>
        )}
      </div>
    </BOLayout>
  );
};

export default BAAuditLogs; 